import { useState, useRef, useEffect, ImgHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

interface ImageSource {
  srcSet: string;
  sizes?: string;
  type: string;
}

interface OptimizedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet'> {
  src: string;
  alt: string;
  webpSrc?: string;
  avifSrc?: string;
  srcSet?: string;
  webpSrcSet?: string;
  avifSrcSet?: string;
  sizes?: string;
  priority?: boolean;
  aspectRatio?: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
}

export const OptimizedImage = ({
  src,
  alt,
  webpSrc,
  avifSrc,
  srcSet,
  webpSrcSet,
  avifSrcSet,
  sizes = '100vw',
  priority = false,
  aspectRatio,
  objectFit = 'cover',
  className,
  width,
  height,
  ...props
}: OptimizedImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const pictureRef = useRef<HTMLPictureElement>(null);

  useEffect(() => {
    if (priority) return; // Skip intersection observer for priority images

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: 0.01,
      }
    );

    if (pictureRef.current) {
      observer.observe(pictureRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const imgStyle: React.CSSProperties = {
    aspectRatio: aspectRatio || undefined,
    objectFit,
  };

  return (
    <picture ref={pictureRef} className={cn('block', className)}>
      {/* AVIF format - best compression */}
      {isInView && avifSrc && (
        <source
          type="image/avif"
          srcSet={avifSrcSet || avifSrc}
          sizes={sizes}
        />
      )}

      {/* WebP format - good compression with wide support */}
      {isInView && webpSrc && (
        <source
          type="image/webp"
          srcSet={webpSrcSet || webpSrc}
          sizes={sizes}
        />
      )}

      {/* JPEG/PNG fallback */}
      <img
        src={isInView ? src : 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"%3E%3C/svg%3E'}
        srcSet={isInView ? srcSet : undefined}
        sizes={sizes}
        alt={alt}
        className={cn(
          'transition-opacity duration-300',
          isLoaded ? 'opacity-100' : 'opacity-0'
        )}
        style={imgStyle}
        onLoad={handleLoad}
        loading={priority ? 'eager' : 'lazy'}
        decoding={priority ? 'sync' : 'async'}
        fetchPriority={priority ? 'high' : 'auto'}
        width={width}
        height={height}
        {...props}
      />
    </picture>
  );
};
